// # Bank Account Profile Component
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../../../auth/state';
import SharedHeader from '../../../../../components/SharedHeader';
import DashboardFooter from '../../../../../components/DashboardFooter';
import bancoCover from '../../../../../aneo_media/finance/banco.jpg';
import budgetIcon from '../../../../../aneo_media/finance/budget.png';
import { doc, getDoc, collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { db } from '../../../../../firebase/config';
import './bancount.css';

// # Import specialized components
import RevenueOverview from './personal_banc/bnct_revenue_overview';
import ProductRevenue from './personal_banc/bnct_product_revenue';
import RevenueTimeline from './personal_banc/bnct_revenue_timeline';
import RevenueAnalytics from './personal_banc/bnct_revenue_analytics';
import RevenueCalculations from './personal_banc/bnct_revenue_calculations';
import BankLogs from './personal_banc/bnct_bank_logs';

// # Tab configuration
const TABS = [
  { id: 'overview', label: 'Revenue Overview' },
  { id: 'products', label: 'Product Revenue' },
  { id: 'timeline', label: 'Revenue Timeline' },
  { id: 'analytics', label: 'Revenue Analytics' },
  { id: 'calculations', label: 'Revenue Calculations' },
  { id: 'logs', label: 'Bank Logs' }
];

// # Project Budget specific tabs
const PROJECT_BUDGET_TABS = [
  { id: 'revenue', label: 'Project Revenue' },
  { id: 'expense', label: 'Project Expenses' },
  { id: 'net', label: 'Net Project Budget' },
  { id: 'calculations', label: 'Budget Calculations' },
  { id: 'logs', label: 'Budget Logs' }
];

// # Team members configuration
const teamMembers = [
  {
    id: 'ahmed_hadidy',
    name: 'Ahmed Elhadidy',
    role: 'CEO - Head of Research & Development Department',
    image: '/icons/team/Elhadidy.png',
    email: 'ahmed.hadidyy@gmail.com'
  },
  {
    id: 'nabil_mostafa',
    name: 'Nabil Mostafa',
    role: 'Head of Finance Department',
    image: '/icons/team/Nabil.png',
    email: 'nabilmostafa14941@gmail.com'
  },
  {
    id: 'ahmed_ayman',
    name: 'Ahmed Ayman',
    role: 'Head of Mentorship & User Experience Department',
    image: '/icons/team/Ayman.jpeg',
    email: 'ahmed.aymaan7474@gmail.com'
  },
  {
    id: 'ahmed_hussein',
    name: 'Ahmed Hussein',
    role: 'Head of Content & Educational Department',
    image: '/icons/team/Hussein.jpeg',
    email: 'Ahmedsehs77@gmail.com'
  },
  {
    id: 'hossam_gomaa',
    name: 'Hossam Gomaa',
    role: 'Head of Technical & Development Department',
    image: '/icons/team/Hossam.jpeg',
    email: 'hossamgomaa487@gmail.com'
  },
  {
    id: 'project-budget',
    name: 'Project Budget',
    role: 'Budget Allocation Account',
    image: budgetIcon,
  }
];

// # Revenue sharing percentages configuration
const revenueShares = {
  // # Library Subscriptions (step1_library, step2_library)
  library: {
    'ahmed_hadidy': 28,
    'ahmed_hussein': 20,
    'ahmed_ayman': 20,
    'hossam_gomaa': 20,
    'nabil_mostafa': 12
  },
  // # Premium Subscriptions (step1_premium, step2_premium)
  premium: {
    'ahmed_hadidy': 22.125,
    'ahmed_hussein': 22.125,
    'ahmed_ayman': 22.125,
    'hossam_gomaa': 22.125,
    'nabil_mostafa': 11.5
  },
  // # Mentorship Monthly (step1_mentorship, step2_mentorship)
  mentorship: {
    egyptian: {
      mentor: 50,      // # Assigned mentor gets 50%
      others: 12.5     // # Others get 12.5% each
    },
    international: {
      mentor: 33,      // # Assigned mentor gets 33%
      others: 16.75    // # Others get 16.75% each
    }
  },
  // # Tutor Monthly (step1_tutor_hosting, step2_tutor_hosting)
  tutor_hosting: {
    tutor: 50        // # Assigned tutor gets 50%, remaining 50% split equally among team
  }
};

// # Helper function to get category key for original amounts
const getCategoryKey = (productType, transaction, memberId) => {
  if (productType.includes('mentorship') || productType === 'oet' || productType === 'us_visa_counselling') {
    // # Determine if this member was the mentor
    const mentorId = transaction.mentor?.uid || '';
    const isMentor = mentorId === memberId;
    
    // # Determine nationality
    const nationality = transaction.user?.nationality || '';
    const isEgyptian = nationality.toLowerCase() === 'egypt' || 
                      nationality.toLowerCase() === 'egyptian' ||
                      nationality.toLowerCase().includes('egypt');
    const nationalityKey = isEgyptian ? 'egyptian' : 'international';
    
    const roleKey = isMentor ? 'as_mentor' : 'as_team_member';
    return `${productType}_${roleKey}_${nationalityKey}`;
  }
  
  return productType;
};

// # Helper function to get share percentage based on member ID and product type
const getSharePercentage = (memberId, productType) => {
  // # Library subscriptions
  if (productType.includes('library')) {
    if (memberId === 'nabil_mostafa') return 12;
    return 20; // # For other team members (hadidy: 28)
  }
  
  // # Premium subscriptions
  if (productType.includes('premium')) {
    if (memberId === 'nabil_mostafa') return 11.5;
    return 22.125; // # For other team members
  }
  
  // # For mentorship, we can't calculate exact percentage without knowing
  // # if they were the mentor and the nationality, so return an average
  return 20; // # Approximate average share
};

// # Calculate member revenue and balances
const calculateMemberRevenue = (financialData, memberId) => {
  // # Initialize revenue data structure
  let revenueData = {
    totalRevenue: 0,          // # Total revenue before budget (gross)
    netRevenue: 0,            // # Net revenue after budget
    revenueByProduct: {},     // # Revenue by product type
    revenueByRole: {},        // # Revenue by role (for mentorship)
    revenueByNationality: {}, // # Revenue by nationality (for mentorship)
    revenueOverTime: {},      // # Revenue over time (by month)
    expenseShare: 0,          // # Share of expenses (for budget account)
    salariesDeducted: 0,      // # Total salaries deducted
    availableCash: 0,         // # Available cash (net revenue - salaries)
    budgetAllocation: 0,      // # Budget allocation (for budget account)
    budgetByProduct: {},      // # Budget by product (for budget account)
    originalAmounts: {},      // # Original amounts for calculations
    mentorshipRevenue: {      // # Mentorship revenue breakdown
      as_mentor: { egyptian: 0, international: 0, total: 0 },
      as_team_member: { egyptian: 0, international: 0, total: 0 }
    },
    paymentLogs: [],          // # Payment logs
    salaryLogs: []            // # Salary logs
  };

  // # Process each month's data
  Object.entries(financialData).forEach(([yearMonth, monthData]) => {
    // # Process payments
    if (monthData.payments) {
      Object.entries(monthData.payments).forEach(([paymentId, payment]) => {
        // # Only process payments where this member has a share
        if (payment.shares && payment.shares[memberId]) {
          const share = payment.shares[memberId];
          const product = payment.pay?.product || 'Unknown';
          const date = new Date(payment.metadata?.created_at || Date.now());
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          
          // # Get share amounts
          const netShareEgp = parseFloat(share.net_share_egp) || 0;
          const totalShareEgp = parseFloat(share.total_share_egp) || 0;
          
          // # Update total revenue (before budget) and net revenue (after budget)
          revenueData.totalRevenue += totalShareEgp;
          revenueData.netRevenue += netShareEgp;
          
          // # Update revenue by product
            if (!revenueData.revenueByProduct[product]) {
              revenueData.revenueByProduct[product] = 0;
            }
          revenueData.revenueByProduct[product] += netShareEgp;

          // # Update revenue over time
            if (!revenueData.revenueOverTime[monthKey]) {
              revenueData.revenueOverTime[monthKey] = {
                revenue: 0,
                expenses: 0,
                salaries: 0,
                net: 0
              };
            }
          revenueData.revenueOverTime[monthKey].revenue += netShareEgp;
          
          // # Add to payment logs
          revenueData.paymentLogs.push({
            id: paymentId,
            amount: netShareEgp,
            totalAmount: totalShareEgp,
            date: payment.metadata?.created_at,
            product: product,
            user: payment.user?.name || 'Unknown User',
            percentage: share.percentage_of_net || 0
          });
          
          // # For mentorship products, track revenue by role and nationality
          if (product.includes('mentorship') || product === 'oet' || product === 'us_visa_counselling') {
            // # Determine if this member was the mentor
            const mentorId = payment.mentor?.uid || '';
            const isMentor = mentorId === memberId;
            
            // # Determine nationality
            const nationality = payment.user?.nationality || '';
            const isEgyptian = nationality.toLowerCase() === 'egypt' || 
                              nationality.toLowerCase() === 'egyptian' ||
                              nationality.toLowerCase().includes('egypt');
            
            // # Add to revenue by role
            const roleKey = isMentor ? 'as_mentor' : 'as_team_member';
            if (!revenueData.revenueByRole[roleKey]) {
              revenueData.revenueByRole[roleKey] = 0;
            }
            revenueData.revenueByRole[roleKey] += netShareEgp;
            
            // # Add to revenue by nationality
            const nationalityKey = isEgyptian ? 'egyptian' : 'international';
            if (!revenueData.revenueByNationality[nationalityKey]) {
              revenueData.revenueByNationality[nationalityKey] = 0;
            }
            revenueData.revenueByNationality[nationalityKey] += netShareEgp;
            
            // # Add to mentorship revenue
            revenueData.mentorshipRevenue[roleKey][nationalityKey] += netShareEgp;
            revenueData.mentorshipRevenue[roleKey].total += netShareEgp;
            
            // # Add to original amounts
            const categoryKey = getCategoryKey(product, payment, memberId);
            if (!revenueData.originalAmounts[categoryKey]) {
              // # Create a new entry with all required fields
              revenueData.originalAmounts[categoryKey] = {
                grossRevenue: 0,
                budgetAllocation: 0,
                netRevenue: 0,
                memberShare: 0,
                sharePercentage: share.percentage_of_net || 0
              };
            }
            
            // # Update original amounts
            const budgetPercentage = payment.budget_dvd?.percentage || 5;
            const grossRevenue = totalShareEgp;
            const budgetAllocation = grossRevenue * (budgetPercentage / 100);
            const netRevenue = netShareEgp;
            
            revenueData.originalAmounts[categoryKey].grossRevenue += grossRevenue;
            revenueData.originalAmounts[categoryKey].budgetAllocation += budgetAllocation;
            revenueData.originalAmounts[categoryKey].netRevenue += netRevenue;
            revenueData.originalAmounts[categoryKey].memberShare += netShareEgp;
          } else {
            // # For non-mentorship products, add to original amounts directly
            if (!revenueData.originalAmounts[product]) {
              // # Create a new entry with all required fields
              revenueData.originalAmounts[product] = {
                grossRevenue: 0,
                budgetAllocation: 0,
                netRevenue: 0,
                memberShare: 0,
                sharePercentage: share.percentage_of_net || 0
              };
            }
            
            // # Update original amounts
            const budgetPercentage = payment.budget_dvd?.percentage || 5;
            const grossRevenue = totalShareEgp;
            const budgetAllocation = grossRevenue * (budgetPercentage / 100);
            const netRevenue = netShareEgp;
            
            revenueData.originalAmounts[product].grossRevenue += grossRevenue;
            revenueData.originalAmounts[product].budgetAllocation += budgetAllocation;
            revenueData.originalAmounts[product].netRevenue += netRevenue;
            revenueData.originalAmounts[product].memberShare += netShareEgp;
          }
          }
        });
      }

    // # Process salaries
      if (monthData.salaries) {
      Object.entries(monthData.salaries).forEach(([salaryId, salary]) => {
        // # Check both reciever and receiver (to handle potential spelling variations)
        const receiver = salary.reciever || salary.receiver;
        if (receiver && receiver.uid === memberId) {
            const amount = parseFloat(salary.amount_egp) || 0;
            revenueData.salariesDeducted += amount;

            const date = new Date(salary.metadata?.created_at || Date.now());
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            if (!revenueData.revenueOverTime[monthKey]) {
              revenueData.revenueOverTime[monthKey] = {
                revenue: 0,
                expenses: 0,
                salaries: amount,
                net: 0
              };
            } else {
              revenueData.revenueOverTime[monthKey].salaries += amount;
            }
          
          // # Add to salary logs
          revenueData.salaryLogs.push({
            id: salaryId,
            amount: amount,
            date: salary.metadata?.created_at,
            notes: salary.metadata?.notes || ''
          });
          }
        });
      }
    });

  // # Calculate available cash
  revenueData.availableCash = revenueData.netRevenue - revenueData.salariesDeducted;

  // # Calculate net for each month in revenue over time
    Object.keys(revenueData.revenueOverTime).forEach(monthKey => {
      const monthData = revenueData.revenueOverTime[monthKey];
      monthData.net = monthData.revenue - monthData.salaries;
    });

    return revenueData;
};

// # Calculate budget account revenue and expenses
const calculateBudgetRevenue = (financialData) => {
  // # Initialize revenue data structure for budget account
  let revenueData = {
    totalRevenue: 0,          // # Total budget allocation
    netRevenue: 0,            // # Net budget after expenses
    revenueByProduct: {},     // # Budget allocation by product
    revenueOverTime: {},      // # Budget allocation over time
    expenseShare: 0,          // # Total expenses
    expensesByCategory: {},   // # Expenses by category
    availableCash: 0,         // # Available budget
    budgetAllocation: 0,      // # Total budget allocation
    budgetByProduct: {},      // # Budget by product
    budgetAllocationLogs: [], // # Budget allocation logs
    expenseLogs: []           // # Expense logs
  };
  
  // # Process each month's data
  Object.entries(financialData).forEach(([yearMonth, monthData]) => {
    // # Process payments for budget allocation
    if (monthData.payments) {
      Object.entries(monthData.payments).forEach(([paymentId, payment]) => {
        if (payment.budget_dvd) {
          const amount = parseFloat(payment.budget_dvd.egp_amount) || 0;
          const product = payment.pay?.product || 'Unknown';
          const date = new Date(payment.metadata?.created_at || Date.now());
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          
          // # Update total budget allocation
          revenueData.totalRevenue += amount;
          revenueData.budgetAllocation += amount;
          
          // # Update budget by product
          if (!revenueData.budgetByProduct[product]) {
            revenueData.budgetByProduct[product] = 0;
          }
          revenueData.budgetByProduct[product] += amount;
          
          // # Update revenue by product (same as budget by product for budget account)
          if (!revenueData.revenueByProduct[product]) {
            revenueData.revenueByProduct[product] = 0;
          }
          revenueData.revenueByProduct[product] += amount;
          
          // # Update budget over time
          if (!revenueData.revenueOverTime[monthKey]) {
            revenueData.revenueOverTime[monthKey] = {
              budget: 0,
              expenses: 0,
              net: 0
            };
          }
          revenueData.revenueOverTime[monthKey].budget += amount;
          
          // # Add to budget allocation logs
          revenueData.budgetAllocationLogs.push({
            id: paymentId,
            amount: amount,
            date: payment.metadata?.created_at,
            product: product,
            user: payment.user?.name || 'Unknown User'
          });
        }
      });
    }
    
    // # Process expenses
    if (monthData.expenses) {
      Object.entries(monthData.expenses).forEach(([expenseId, expense]) => {
        const amount = parseFloat(expense.egp_total) || 0;
        const category = expense.category || 'Unknown';
        const date = new Date(expense.metadata?.created_at || Date.now());
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        // # Update total expenses
        revenueData.expenseShare += amount;
        
        // # Update expenses by category
        if (!revenueData.expensesByCategory[category]) {
          revenueData.expensesByCategory[category] = 0;
        }
        revenueData.expensesByCategory[category] += amount;
        
        // # Update expenses over time
        if (!revenueData.revenueOverTime[monthKey]) {
          revenueData.revenueOverTime[monthKey] = {
            budget: 0,
            expenses: 0,
            net: 0
          };
        }
        revenueData.revenueOverTime[monthKey].expenses += amount;
        
        // # Add to expense logs
        revenueData.expenseLogs.push({
          id: expenseId,
          amount: amount,
          date: expense.metadata?.created_at,
          category: category,
          details: expense.details || ''
        });
      });
    }
  });
  
  // # Calculate net budget and available cash
  revenueData.netRevenue = revenueData.totalRevenue - revenueData.expenseShare;
  revenueData.availableCash = revenueData.netRevenue;
  
  // # Calculate net for each month in revenue over time
  Object.keys(revenueData.revenueOverTime).forEach(monthKey => {
    const monthData = revenueData.revenueOverTime[monthKey];
    monthData.net = monthData.budget - monthData.expenses;
  });
  
  return revenueData;
};

const BankAccount = () => {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuthStore();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [revenueData, setRevenueData] = useState(null);

  // # Find member by ID
  const member = teamMembers.find((m) => 
    m.id.toLowerCase() === memberId?.toLowerCase()
  );

  // # Fetch revenue data
  useEffect(() => {
    const fetchRevenueData = async () => {
      if (!member) return;
      
      try {
        setLoading(true);
        
        // # Get all documents from the financial collection
        const financialRef = collection(db, 'financial');
        const financialSnap = await getDocs(financialRef);
        
        // # Initialize financial data structure
        const financialData = {};
        
        // # Process each document (year_month format)
        financialSnap.forEach(doc => {
          const yearMonthData = doc.data();
          const yearMonth = doc.id;
          
          // # Initialize financial data structure for this month
          financialData[yearMonth] = {
            payments: {},
            expenses: {},
            salaries: {}
          };
          
          // # Process payments
          if (yearMonthData.payments) {
            Object.entries(yearMonthData.payments).forEach(([paymentId, payment]) => {
              financialData[yearMonth].payments[paymentId] = payment;
            });
          }
          
          // # Process expenses
          if (yearMonthData.expenses) {
            Object.entries(yearMonthData.expenses).forEach(([expenseId, expense]) => {
              financialData[yearMonth].expenses[expenseId] = expense;
            });
          }
          
          // # Process salaries
          if (yearMonthData.salaries) {
            Object.entries(yearMonthData.salaries).forEach(([salaryId, salary]) => {
              financialData[yearMonth].salaries[salaryId] = salary;
            });
          }
        });
        
        // # Calculate revenue data based on member type
        let calculatedRevenueData;
        
          if (member.id === 'project-budget') {
          // # For project budget account
          calculatedRevenueData = calculateBudgetRevenue(financialData);
          } else {
          // # For team members
          calculatedRevenueData = calculateMemberRevenue(financialData, member.id);
        }
        
        // # Set revenue data
        setRevenueData(calculatedRevenueData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching revenue data:', error);
        setLoading(false);
      }
    };

    fetchRevenueData();
  }, [member]);

  // # Redirect if not admin or invalid member
  React.useEffect(() => {
    if (!isAdmin) {
      navigate('/admin/financial/bank');
    } else if (!member && memberId) {
      console.error('Invalid member ID:', memberId);
      navigate('/admin/financial/bank');
    }
  }, [isAdmin, member, memberId, navigate]);

  if (loading) return <div className="bnct_loading">Loading financial data...</div>;
  if (!member) return null;

  // # Determine which tabs to use
  const currentTabs = member.id === 'project-budget' ? PROJECT_BUDGET_TABS : TABS;

  // # Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
      case 'revenue':
        return <RevenueOverview revenueData={revenueData} member={member} />;

      case 'products':
      case 'expense':
        return <ProductRevenue revenueData={revenueData} member={member} />;

      case 'timeline':
      case 'net':
        return <RevenueTimeline revenueData={revenueData} member={member} />;

      case 'analytics':
        return <RevenueAnalytics revenueData={revenueData} member={member} />;

      case 'calculations':
        return <RevenueCalculations revenueData={revenueData} member={member} />;

      case 'logs':
        return <BankLogs revenueData={revenueData} member={member} />;

      default:
        return null;
    }
  };

  return (
    <div className="bnct_page">
      <SharedHeader />
      
      <div className="bnct_cover">
        <img src={bancoCover} alt="Bank Account Cover" className="bnct_cover-img" />
        <div className="bnct_profile-overlay">
          <div className="bnct_profile-image">
            <img src={member.image} alt={member.name} />
          </div>
          <div className="bnct_profile-info">
            <h1>{member.name}</h1>
            <p className="bnct_role">{member.role}</p>
          </div>
        </div>
      </div>

      <main className="bnct_content">
        <div className="bnct_tabs">
          {currentTabs.map((tab) => (
            <button
              key={tab.id}
              className={`bnct_tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="bnct_tab-container">
          {loading ? (
            <div className="bnct_loading">Loading financial data...</div>
          ) : (
            renderTabContent()
          )}
        </div>
      </main>

      <DashboardFooter />
    </div>
  );
};

export default BankAccount;
